import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { ChevronRight, Home } from 'lucide-react';

import type { FolderBreadcrumbItem } from '@/types/SixDetail.types';
import { classNames } from '@/utils/classNames.utils';

interface DroppableBreadcrumbSegmentProps {
  item: FolderBreadcrumbItem & { onClick?: () => void };
  isLast: boolean;
  droppableId: string;
}

function DroppableBreadcrumbSegment({
  item,
  isLast,
  droppableId,
}: DroppableBreadcrumbSegmentProps): React.ReactElement {
  const { setNodeRef, isOver } = useDroppable({ id: droppableId });

  return (
    <span
      ref={setNodeRef}
      className={classNames(
        'rounded px-1.5 py-0.5 transition-colors',
        isOver && 'bg-primary/10 ring-1 ring-primary/40',
        isLast ? 'text-foreground font-medium' : 'text-muted-foreground hover:text-foreground cursor-pointer',
      )}
      onClick={item.onClick}
    >
      {item.label}
    </span>
  );
}

interface DroppableFolderBreadcrumbProps {
  items: Array<FolderBreadcrumbItem & { onClick?: () => void }>;
  onNavigateRoot?: () => void;
  className?: string;
}

function DroppableFolderBreadcrumb({
  items,
  onNavigateRoot,
  className,
}: DroppableFolderBreadcrumbProps): React.ReactElement {
  const { setNodeRef: setRootRef, isOver: isRootOver } = useDroppable({
    id: 'breadcrumb:root',
  });

  return (
    <nav
      className={classNames('flex items-center gap-1 px-3 py-2 sm:px-6 text-sm', className)}
      aria-label="Breadcrumb"
    >
      <span
        ref={setRootRef}
        className={classNames(
          'rounded px-1.5 py-0.5 transition-colors cursor-pointer text-muted-foreground hover:text-foreground',
          isRootOver && 'bg-primary/10 ring-1 ring-primary/40',
        )}
        onClick={onNavigateRoot}
      >
        <Home className="h-4 w-4" />
      </span>

      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        const droppableId = item.path !== undefined ? `breadcrumb:${item.path}` : 'breadcrumb:sixroot';

        return (
          <React.Fragment key={item.id}>
            <ChevronRight className="h-3 w-3 text-muted-foreground flex-shrink-0" />
            <DroppableBreadcrumbSegment
              item={item}
              isLast={isLast}
              droppableId={droppableId}
            />
          </React.Fragment>
        );
      })}
    </nav>
  );
}

export default DroppableFolderBreadcrumb;
